/**
 * ThemePreviewCard - Sandboxed Theme Preview Component
 * 
 * ARCHITECTURE INVARIANT (per theme-architecture.md V2):
 * This component uses INLINE STYLES to render a theme preview.
 * It does NOT call setTheme() or mutate global CSS variables.
 * 
 * Purpose: Let users "see before they commit" during onboarding
 * without causing flicker or global side effects.
 */

import { Star, Play, Clock, Flame, TrendingUp } from 'lucide-react';
import { type ThemeDefinition } from '@/config/themes';
import { cn } from '@/lib/utils';

interface ThemePreviewCardProps {
  theme: ThemeDefinition;
  className?: string;
}

export function ThemePreviewCard({ theme, className }: ThemePreviewCardProps) {
  const { hexColors, name } = theme;
  
  // Determine if this is a light theme by checking background luminance
  const isLightTheme = isLightColor(hexColors.bg);
  
  return (
    <div
      className={cn(
        "rounded-xl overflow-hidden border shadow-lg transition-all duration-300",
        className
      )}
      style={{
        backgroundColor: hexColors.bg,
        borderColor: hexColors.border,
      }}
    >
      {/* App Header Bar */}
      <div
        className="flex items-center justify-between px-4 py-3 border-b"
        style={{
          backgroundColor: hexColors.card,
          borderColor: hexColors.border,
        }}
      >
        <div className="flex items-center gap-2">
          <div 
            className="w-7 h-7 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: hexColors.primary }}
          >
            <Flame className="w-4 h-4" style={{ color: isLightTheme ? '#FFFFFF' : hexColors.bg }} />
          </div>
          <span 
            className="font-semibold text-sm"
            style={{ color: hexColors.text }}
          >
            StudyForge
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <Star className="w-4 h-4" style={{ color: hexColors.primary }} />
          <span 
            className="text-sm font-medium"
            style={{ color: hexColors.text }}
          >
            24
          </span>
        </div>
      </div>
      
      {/* App Content Area */}
      <div className="p-4 space-y-3">
        {/* Stats Row */}
        <div className="flex gap-2">
          {/* Stat Card 1 */}
          <div
            className="flex-1 p-3 rounded-lg border"
            style={{
              backgroundColor: hexColors.card,
              borderColor: hexColors.border,
            }}
          >
            <div className="flex items-center gap-1.5 mb-1">
              <Clock className="w-3.5 h-3.5" style={{ color: hexColors.primary }} />
              <span 
                className="text-xs font-medium"
                style={{ color: hexColors.textMuted }}
              >
                Today
              </span>
            </div>
            <div 
              className="text-lg font-bold"
              style={{ color: hexColors.text }}
            >
              2h 15m
            </div>
          </div>
          
          {/* Stat Card 2 */}
          <div
            className="flex-1 p-3 rounded-lg border"
            style={{
              backgroundColor: hexColors.card,
              borderColor: hexColors.border,
            }}
          >
            <div className="flex items-center gap-1.5 mb-1">
              <TrendingUp className="w-3.5 h-3.5" style={{ color: hexColors.primary }} />
              <span 
                className="text-xs font-medium"
                style={{ color: hexColors.textMuted }}
              >
                Streak
              </span>
            </div>
            <div 
              className="text-lg font-bold"
              style={{ color: hexColors.text }}
            >
              7 days
            </div>
          </div>
        </div>
        
        {/* Primary Action Button */}
        <button
          className="w-full py-3 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 transition-transform hover:scale-[1.02] active:scale-[0.98]"
          style={{
            backgroundColor: hexColors.primary,
            color: isLightPrimary(hexColors.primary) ? hexColors.bg : '#FFFFFF',
          }}
        >
          <Play className="w-4 h-4" />
          Start Session
        </button>
      </div>
      
      {/* Theme Name Label */}
      <div
        className="px-4 py-2 border-t text-center"
        style={{
          backgroundColor: hexColors.card,
          borderColor: hexColors.border,
        }}
      >
        <span 
          className="text-xs font-medium"
          style={{ color: hexColors.textMuted }}
        >
          {name}
        </span>
      </div>
    </div>
  );
}

/**
 * Helper: Determine if a hex color is light (for text contrast)
 */
function isLightColor(hex: string): boolean {
  const rgb = hexToRgb(hex);
  if (!rgb) return false;
  // Using relative luminance formula
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luminance > 0.5;
}

/**
 * Helper: Determine if primary color needs dark text
 */
function isLightPrimary(hex: string): boolean {
  const rgb = hexToRgb(hex);
  if (!rgb) return false;
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luminance > 0.6;
}

/**
 * Helper: Convert hex to RGB
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

export default ThemePreviewCard;
